import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Grid,
    TextField,
    Typography,
    Button,
    Box,
    makeStyles,
    MenuItem
} from "@material-ui/core";
import MuiPhoneNumber from "material-ui-phone-number";
import axios from "axios";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Link } from "react-router-dom";
import { api} from "../functions/Helper";

const useStyles = makeStyles((theme) => ({
    textField: {
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: theme.palette.grey[400] },
            "&:hover fieldset": { borderColor: theme.palette.primary.main },
            "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
            "&.Mui-error fieldset": { borderColor: theme.palette.error.main },
        },
        "& .MuiFormHelperText-root.Mui-error": {
            color: theme.palette.error.main,
        },
    },
    submitButton: {
        backgroundColor: '#f9bb02',
        borderRadius: '50px',
        color: 'black',
        fontSize: '1.2rem',
        marginLeft: '16px',
        fontWeight: '600',
        padding: '4px 16px',
        border: 'none',
        fontFamily: 'MyCustomFont, sans-serif',
        '&:hover': {
            boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
            backgroundColor: "#e7be46",
            transform: "translateY(-2px)",
            cursor: 'pointer',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.9rem',
        padding: '6px 6px',
        },
    },
}));

const countries = [
    { code: "", label: "Select Country" },
    { code: "US", label: "United States" },
    { code: "IN", label: "India" },
    { code: "GB", label: "United Kingdom" },
    { code: "CA", label: "Canada" },
    { code: "AU", label: "Australia" },
    { code: "DE", label: "Germany" },
    { code: "FR", label: "France" },
    { code: "SG", label: "Singapore" },
    { code: "AE", label: "United Arab Emirates" },
    { code: "NL", label: "Netherlands" },
];

const AvailServices = () => {
    const classes = useStyles();

    const [formData, setFormData] = useState({
        fname: "",
        lname: "",
        email: "",
        phone: "",
        country: "",
    });

    const [msg, setMsg] = useState(false);
    const [msgStatus, setMsgStatus] = useState("success.main");
    const [formErrors, setFormErrors] = useState({});
    const [phoneCountry, setPhoneCountry] = useState("US");

    const validateField = (name, value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let error = "";

        switch (name) {
            case "fname":
            case "lname":
                if (!value.trim()) {
                    error = `${name === "fname" ? "First" : "Last"} name is required`;
                } else if (!/^[A-Za-z\s]+$/.test(value)) {
                    error = `${name === "fname" ? "First" : "Last"} name can only contain letters`;
                }
                break;
            case "email":
                if (!value.trim()) error = "Email is required";
                else if (!emailRegex.test(value)) error = "Invalid email format";
                break;
            case "phone":
                if (!value.trim()) error = "Phone number is required";
                else {
                    const phoneNumber = parsePhoneNumberFromString(value, phoneCountry);
                    if (!phoneNumber || !phoneNumber.isValid()) error = "Phone number is invalid";
                }
                break;
            case "country":
                if (!value) error = "Country is required";
                break;
            default:
                break;
        }

        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handlePhoneChange = (value, countryData) => {
        setFormData((prev) => ({ ...prev, phone: value }));
        setPhoneCountry(countryData.countryCode);
        if (formErrors.phone) {
            setFormErrors((prev) => ({ ...prev, phone: validateField("phone", value) }));
        }
    };

    const validateForm = () => {
        const errors = {};
        for (const field in formData) {
            const error = validateField(field, formData[field]);
            if (error) errors[field] = error;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

	const sendPdf = () => {
		const payload = {
			fname: formData.fname,
			lname: formData.lname,
			email: formData.email,
			phone: formData.phone,
			country: formData.country,
		};

		console.log("Sending payload:", payload);

		axios.post(
			api + '/send-pdf-mail',
			new URLSearchParams(payload),
			{ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
		)
		.then((res) => {
			console.log("Email sent:", res.data);
			setMsgStatus("success.main");
			setMsg("PDF has been shared to your mail. Kindly check your inbox to download it.");
		})
		.catch((err) => {
			console.error("Error sending email:", err);
			setMsgStatus("error.main");
			setMsg("Something went wrong. Please try again later.");
		});
	};

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            sendPdf();
            setFormData({
                fname: "",
                lname: "",
                email: "",
                phone: "",
                country: "",
            });
            setFormErrors({});
        }
    };

    return (
	<Box marginTop={4}>
        <Grid container spacing={0}>
            <Grid item xs={12} md={5}>
               	<Box padding={3} borderRadius="12px">
				<img 
				src="/assets/images/lead-gen.jpeg" 
				alt="" 
				height="400" 
				width="100%" 
				style={{'object-fit':'cover',borderRadius:"inherit"}}
				/>
				</Box>
            </Grid>

            <Grid item xs={12} md={7}>
                <Box padding={4} paddingBottom={1}>
                    {
						msg && 
						(
							<Box color={msgStatus} fontSize="14px" mb={1}>{msg}</Box>
						)
					}
					
                    <Typography variant="h5">
					<Box fontWeight="bold">Automate Your Lead Gen</Box>
					</Typography>
                </Box>
                <Box padding={4}>
                <form onSubmit={handleFormSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First Name"
                                name="fname"
                                variant="outlined"
                                fullWidth
                                value={formData.fname}
                                onChange={handleInputChange}
                                error={!!formErrors.fname}
                                helperText={formErrors.fname}
                                className={classes.textField}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Last Name"
                                name="lname"
                                variant="outlined"
                                fullWidth
                                value={formData.lname}
                                onChange={handleInputChange}
                                error={!!formErrors.lname}
                                helperText={formErrors.lname}
                                className={classes.textField}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Email"
                                name="email"
                                variant="outlined"
                                fullWidth
                                value={formData.email}
                                onChange={handleInputChange}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                className={classes.textField}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MuiPhoneNumber
                                defaultCountry="us"
                                label="Phone Number"
                                variant="outlined"
                                fullWidth
                                name="phone"
                                value={formData.phone}
                                onChange={handlePhoneChange}
                                error={!!formErrors.phone}
                                helperText={formErrors.phone}
                                className={classes.textField}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Country"
                                name="country"
                                variant="outlined"
                                fullWidth
                                value={formData.country}
                                onChange={handleInputChange}
                                error={!!formErrors.country}
                                helperText={formErrors.country}
                                className={classes.textField}
                                required
                            >
                                {countries.map((option) => (
                                    <MenuItem key={option.code} value={option.label}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                           
						   <Box display="flex" justifyContent="center" mt={2}>
							  <Button
								variant="contained"
								type="submit"
								className={classes.submitButton}
							  >
								Download Now
							  </Button>
							</Box>

						   
                        </Grid>
                    </Grid>
                </form>
				</Box>
            </Grid>
        </Grid>
        </Box>
    );
};

export default AvailServices;
